import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useAgents / useBanks (SOUND-672 / backend SOUND-671) — the two counterparty
 * registries either side of a processor in the AGENT -> PROCESSOR -> SPONSOR
 * BANK chain. Internal-only GLOBAL config, same shape as `useSlaPolicies` /
 * `useFunctions`: plain useState/useEffect + makeApiRequest (house pattern, not
 * React Query), CompanyHeader from useEffectiveCompanyId().
 *
 *   GET /agents                          → { data: [agent] }   (active only)
 *   GET /agents?includeInactive=true     → { data: [agent] }   (all)
 *   GET /banks                           → { data: [bank] }
 *
 * The two collections are field-for-field identical, so one factory serves
 * both rather than two copies that drift.
 */
function makeRegistryHook(listRoute) {
  return function useRegistry({ includeInactive = false, enabled = true } = {}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);

    const auth = useAuth();
    const companyId = useEffectiveCompanyId();

    const fetchItems = useCallback(async () => {
      if (!enabled || !companyId || !auth?.user) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await makeApiRequest(
          "get",
          listRoute(includeInactive ? { includeInactive: "true" } : {}),
          auth.user,
          companyId
        );
        setItems(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }, [enabled, companyId, auth?.user, includeInactive]);

    useEffect(() => {
      fetchItems();
    }, [fetchItems]);

    return { items, loading, error, refetch: fetchItems };
  };
}

export const useAgents = makeRegistryHook(apiRoutes.listAgents);
export const useBanks = makeRegistryHook(apiRoutes.listBanks);
